/**
 * TicketManualModal — Regularización manual de tickets por el agente.
 *
 * Permite crear un ticket que llegó por un canal no oficial, a nombre del
 * cliente correspondiente. Nace en estado ASIGNADO (responsable + prioridad).
 * Header y footer sticky; solo el cuerpo hace scroll.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';
import { useAuth } from '../../../context/AuthContext';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Loader2, Search, Paperclip, X, UserPlus, AlertCircle, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import ClasificacionCascadeSelect from './ClasificacionCascadeSelect';
import {
  TIPOS_DOC, validarDoc, validarEmail, validarCelular,
} from './clienteValidators';

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = 'application/pdf,image/*,video/*';
const MIME_OK = [
  'application/pdf',
  'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif',
  'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo',
];

const initialState = () => ({
  cliente_tipo_documento: 'DNI',
  cliente_documento: '',
  cliente_email: '',
  cliente_nombre: '',
  cliente_celular: '+51 ',
  canal_id: '',
  asunto: '',
  descripcion: '',
});

function depthOf(tree, id) {
  if (!id) return 0;
  const byId = new Map((tree || []).map((n) => [n.id, n]));
  let d = 0;
  let cur = byId.get(id);
  const seen = new Set();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    d += 1;
    cur = cur.parent_id ? byId.get(cur.parent_id) : null;
  }
  return d;
}

export default function TicketManualModal({
  open, onClose, responsables = [], prioridades = [], canales = [], onCreated,
}) {
  const { api } = useAuth();
  const [data, setData] = useState(initialState);
  const [tree, setTree] = useState([]);
  const [clasificacionId, setClasificacionId] = useState(null);
  const [responsableId, setResponsableId] = useState('');
  const [prioridadId, setPrioridadId] = useState('');
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTree, setLoadingTree] = useState(true);
  const fileRef = useRef(null);

  const canalesActivos = useMemo(
    () => (canales || []).filter((c) => c.activo !== false),
    [canales],
  );

  // Cargar árbol + defaults al abrir
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setLoadingTree(true);
    api.get('/tickets/clasificacion/tree', { signal: controller.signal })
      .then((r) => setTree(r.data || []))
      .catch((e) => { if (!isCanceled(e)) toast.error('No se pudo cargar la clasificación'); })
      .finally(() => { if (!controller.signal.aborted) setLoadingTree(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Prioridad por defecto: se fija cuando el modal está abierto y llegan las
  // prioridades (resincroniza si el catálogo cargó después de abrir el modal).
  useEffect(() => {
    if (!open || prioridadId) return;
    const def = (prioridades || []).find((p) => p.es_default) || (prioridades || [])[0];
    if (def) setPrioridadId(def.id);
  }, [open, prioridades, prioridadId]);

  // Reset al cerrar
  useEffect(() => {
    if (open) return;
    setData(initialState());
    setClasificacionId(null);
    setResponsableId('');
    setPrioridadId('');
    setFiles([]);
    setErrors({});
    setServerError('');
    setSearching(false);
    setSubmitting(false);
  }, [open]);

  const nivelClasificacion = useMemo(() => depthOf(tree, clasificacionId), [tree, clasificacionId]);

  const buscarHabilitado = !!(
    data.cliente_email.trim()
    || (data.cliente_tipo_documento && data.cliente_documento.trim())
  );

  const buscarCliente = async () => {
    if (!buscarHabilitado || searching) return;
    setSearching(true);
    try {
      const { data: res } = await api.post('/tickets/manual/buscar-cliente', {
        tipo_documento: data.cliente_tipo_documento,
        documento: data.cliente_documento.trim() || undefined,
        email: data.cliente_email.trim() || undefined,
      });
      if (res.encontrado && res.cliente) {
        const c = res.cliente;
        setData((d) => ({
          ...d,
          cliente_nombre: c.nombre || d.cliente_nombre,
          cliente_email: c.email || d.cliente_email,
          cliente_celular: c.celular || d.cliente_celular,
          cliente_documento: c.documento || d.cliente_documento,
          cliente_tipo_documento: c.tipo_documento || d.cliente_tipo_documento,
        }));
        toast.success('Cliente identificado. Verifica los datos.');
      } else {
        toast.info('No se encontró el cliente. Completa todos sus datos.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo buscar el cliente.');
    } finally {
      setSearching(false);
    }
  };

  const onPickFiles = (ev) => {
    const picked = Array.from(ev.target.files || []);
    const nuevos = [];
    for (const f of picked) {
      if (f.size > MAX_BYTES) { toast.error(`"${f.name}" supera los 20MB.`); continue; }
      if (!MIME_OK.includes(f.type)) { toast.error(`"${f.name}": solo PDF, imágenes y videos.`); continue; }
      nuevos.push(f);
    }
    setFiles((prev) => [...prev, ...nuevos]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const quitarFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const validar = () => {
    const e = {};
    const docErr = validarDoc(data.cliente_tipo_documento, data.cliente_documento);
    if (docErr) e.cliente_documento = docErr;
    const emErr = validarEmail(data.cliente_email);
    if (emErr) e.cliente_email = emErr;
    const nm = (data.cliente_nombre || '').trim();
    if (nm.length < 3) e.cliente_nombre = 'Ingresa el nombre completo.';
    else if (!nm.includes(' ')) e.cliente_nombre = 'Incluye nombre y apellido.';
    const celErr = validarCelular(data.cliente_celular);
    if (celErr) e.cliente_celular = celErr;
    if (!data.canal_id) e.canal_id = 'Selecciona un canal.';
    const asunto = (data.asunto || '').trim();
    if (asunto.length < 3) e.asunto = 'El asunto es obligatorio (mínimo 3 caracteres).';
    const desc = (data.descripcion || '').trim();
    if (desc.length < 10) e.descripcion = 'La descripción es obligatoria (mínimo 10 caracteres).';
    if (!clasificacionId || nivelClasificacion < 2) e.clasificacion = 'La clasificación debe ser mínimo de nivel 2.';
    if (!responsableId) e.responsable = 'Selecciona un responsable.';
    if (!prioridadId) e.prioridad = 'Selecciona una prioridad.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const crear = async () => {
    if (submitting) return;
    setServerError('');
    if (!validar()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('canal_id', data.canal_id);
      fd.append('asunto', data.asunto.trim());
      fd.append('descripcion', data.descripcion.trim());
      fd.append('clasificacion_id', clasificacionId);
      fd.append('responsable_id', responsableId);
      fd.append('prioridad_id', prioridadId);
      fd.append('cliente_nombre', data.cliente_nombre.trim());
      fd.append('cliente_email', data.cliente_email.trim());
      fd.append('cliente_celular', data.cliente_celular.trim());
      fd.append('cliente_tipo_documento', data.cliente_tipo_documento);
      fd.append('cliente_documento', data.cliente_documento.trim());
      files.forEach((f) => fd.append('adjuntos', f));
      const { data: res } = await api.post('/tickets/manual', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ticket ${res.codigo} creado y asignado`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : 'No se pudo crear el ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (ev) => {
    const v = ev.target.value;
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const clearErr = (k) => setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 flex flex-col max-h-[90dvh] overflow-hidden"
        data-testid="ticket-manual-modal"
      >
        {/* Header sticky */}
        <div className="shrink-0 border-b px-6 py-4 bg-background">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-5 h-5 text-emerald-700" />
            Nuevo ticket
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Regulariza un ticket recibido por un canal no oficial, a nombre del cliente.
          </DialogDescription>
        </div>

        {/* Cuerpo scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="ticket-manual-body">
          {/* ---- Cliente ---- */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cliente</h3>
            <div className="grid grid-cols-[200px_1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Tipo de documento</Label>
                <Select
                  value={data.cliente_tipo_documento}
                  onValueChange={(v) => { setData((d) => ({ ...d, cliente_tipo_documento: v, cliente_documento: '' })); clearErr('cliente_documento'); }}
                >
                  <SelectTrigger data-testid="manual-select-tipo-doc"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOC.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Número de documento</Label>
                <Input
                  value={data.cliente_documento}
                  onChange={(e) => { setData((d) => ({ ...d, cliente_documento: e.target.value.toUpperCase() })); clearErr('cliente_documento'); }}
                  placeholder={data.cliente_tipo_documento === 'DNI' ? '12345678' : 'Número'}
                  data-testid="manual-input-documento"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={buscarCliente}
                disabled={!buscarHabilitado || searching}
                data-testid="manual-btn-buscar-cliente"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-1">Buscar</span>
              </Button>
            </div>
            {errors.cliente_documento && <p className="text-xs text-red-600">{errors.cliente_documento}</p>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Correo electrónico</Label>
                <Input
                  type="email" value={data.cliente_email} onChange={set('cliente_email')}
                  placeholder="cliente@correo.com" data-testid="manual-input-email"
                />
                {errors.cliente_email && <p className="text-xs text-red-600">{errors.cliente_email}</p>}
              </div>
              <div>
                <Label className="text-xs">Celular</Label>
                <Input
                  value={data.cliente_celular} onChange={set('cliente_celular')}
                  placeholder="+51 999 888 777" data-testid="manual-input-celular"
                />
                {errors.cliente_celular && <p className="text-xs text-red-600">{errors.cliente_celular}</p>}
              </div>
            </div>
            <div>
              <Label className="text-xs">Nombre completo</Label>
              <Input
                value={data.cliente_nombre} onChange={set('cliente_nombre')}
                placeholder="Nombre y apellido" data-testid="manual-input-nombre"
              />
              {errors.cliente_nombre && <p className="text-xs text-red-600">{errors.cliente_nombre}</p>}
            </div>
          </section>

          {/* ---- Ticket ---- */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ticket</h3>
            <div>
              <Label className="text-xs">Canal</Label>
              <Select value={data.canal_id} onValueChange={(v) => { setData((d) => ({ ...d, canal_id: v })); clearErr('canal_id'); }}>
                <SelectTrigger data-testid="manual-select-canal">
                  <SelectValue placeholder="Selecciona el canal de origen" />
                </SelectTrigger>
                <SelectContent>
                  {canalesActivos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.canal_id && <p className="text-xs text-red-600">{errors.canal_id}</p>}
            </div>
            <div>
              <Label className="text-xs">Asunto</Label>
              <Input
                value={data.asunto} onChange={set('asunto')} maxLength={200}
                placeholder="Resumen del caso" data-testid="manual-input-asunto"
              />
              {errors.asunto && <p className="text-xs text-red-600">{errors.asunto}</p>}
            </div>
            <div>
              <Label className="text-xs">Descripción</Label>
              <Textarea
                value={data.descripcion} onChange={set('descripcion')} rows={4} maxLength={5000}
                placeholder="Describe el pedido del cliente..." data-testid="manual-textarea-descripcion"
              />
              {errors.descripcion && <p className="text-xs text-red-600">{errors.descripcion}</p>}
            </div>
            <div>
              <Label className="text-xs">Clasificación (mínimo nivel 2)</Label>
              {loadingTree ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
                </div>
              ) : (
                <div data-testid="manual-clasificacion">
                  <ClasificacionCascadeSelect
                    tree={tree}
                    value={clasificacionId}
                    hideActions
                    onLiveChange={(id) => { setClasificacionId(id); clearErr('clasificacion'); }}
                  />
                </div>
              )}
              {errors.clasificacion && <p className="text-xs text-red-600">{errors.clasificacion}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Asignar a (responsable)</Label>
                <Select value={responsableId} onValueChange={(v) => { setResponsableId(v); clearErr('responsable'); }}>
                  <SelectTrigger data-testid="manual-select-responsable">
                    <SelectValue placeholder="Selecciona un responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    {responsables.map((r) => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.responsable && <p className="text-xs text-red-600">{errors.responsable}</p>}
              </div>
              <div>
                <Label className="text-xs">Prioridad</Label>
                <Select value={prioridadId} onValueChange={(v) => { setPrioridadId(v); clearErr('prioridad'); }} disabled={(prioridades || []).length === 0}>
                  <SelectTrigger data-testid="manual-select-prioridad">
                    <SelectValue placeholder={(prioridades || []).length === 0 ? 'Cargando…' : 'Selecciona una prioridad'} />
                  </SelectTrigger>
                  <SelectContent>
                    {prioridades.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.horas_sla}h)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.prioridad && <p className="text-xs text-red-600">{errors.prioridad}</p>}
              </div>
            </div>
          </section>

          {/* ---- Adjuntos ---- */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Adjuntos (opcional)</h3>
            <input
              ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onPickFiles}
              className="hidden" data-testid="manual-input-adjuntos"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="manual-btn-adjuntar">
              <Paperclip className="w-4 h-4 mr-1" /> Adjuntar archivos
            </Button>
            <p className="text-[11px] text-slate-400">PDF, imágenes y videos. Máx. 20MB por archivo.</p>
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1" data-testid={`manual-file-${i}`}>
                    <span className="truncate">{f.name} <span className="text-slate-400">({(f.size / 1024 / 1024).toFixed(1)}MB)</span></span>
                    <button type="button" onClick={() => quitarFile(i)} className="text-slate-400 hover:text-red-600" data-testid={`manual-file-remove-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {serverError && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" role="alert" data-testid="manual-server-error">
              <AlertCircle className="w-4 h-4 shrink-0" /> {serverError}
            </div>
          )}
        </div>

        {/* Footer sticky */}
        <div className="shrink-0 border-t px-6 py-4 bg-background flex justify-end gap-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={submitting} data-testid="manual-btn-cancelar">
            Cancelar
          </Button>
          <Button onClick={crear} disabled={submitting} data-testid="manual-btn-crear">
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creando…</>
              : <><Check className="w-4 h-4 mr-1" /> Crear ticket</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
